/**
 * @file - This is intended to be a centralized service for injecting all dependencies into a Vue app.
 * It helps keep them consistent for unit/e2e testing and Storybook environments.
 */

import { createPinia } from "pinia";
import router from "../router";
import { VueQueryPlugin } from "@tanstack/vue-query";
import * as Sentry from "@sentry/vue";
import {
  library,
  type IconDefinition
} from "@fortawesome/fontawesome-svg-core";
import {
  FontAwesomeIcon,
  FontAwesomeLayers,
  FontAwesomeLayersText
} from "@fortawesome/vue-fontawesome";
import {
  faGoogle,
  faMicrosoft,
  faSalesforce,
  faHubspot
} from "@fortawesome/free-brands-svg-icons";
import {
  faAddressCard,
  faCalendarWeek,
  faCheck,
  faCopy,
  faExchange,
  faEye,
  faPlus,
  faSignOut,
  faSquare,
  faTrash,
  faTriangleExclamation,
  faUserPlus
} from "@fortawesome/free-solid-svg-icons";
import { type App, type Directive, type Plugin } from "vue";
import PrimeVue, { type PrimeVueConfiguration } from "primevue/config";

// Import Highcharts - In v12+, modules self-initialize on import
import HighChartsPlugin from "highcharts-vue";
import "highcharts/modules/funnel";
import "highcharts/modules/accessibility";
import "highcharts/modules/draggable-points";
import "highcharts/modules/sankey";
import "highcharts/modules/organization";

// Import services
import ConfirmationService from "primevue/confirmationservice";
import ToastService from "primevue/toastservice";

// Import directives
import { relativeTimeDirective } from "@/directives/relative-time.directive";
import { cellFormatterDirective } from "@/directives/cell-formatter.directive";
import { vAutofocus } from "@/directives/autofocus.directive";

// Default css
import "../assets/scss/index.scss";

//Primevue theme
import { defaultTheme, taiPreset } from "@/theme/default";
import { taiTooltip } from "@/directives/tooltip/tooltip.directive";

/**
 * Utility class for injecting components, icons and plugins to Vue app
 */
export class AppRegistryHelper<T> {
  app: App<T>;
  readonly fontAwesomeIcons: IconDefinition[];
  readonly components = {};
  readonly plugins: Array<{ plugin: Plugin; config: unknown }>;
  readonly directives: Record<string, Directive>;

  constructor(app: App<T>) {
    this.app = app;

    this.fontAwesomeIcons = [
      // Add FA icons here:
      faGoogle,
      faMicrosoft,
      faSalesforce,
      faHubspot,
      faAddressCard,
      faCalendarWeek,
      faCheck,
      faCopy,
      faExchange,
      faEye,
      faPlus,
      faSignOut,
      faSquare,
      faTrash,
      faTriangleExclamation,
      faUserPlus
    ];

    this.components = {
      // Add third party components:
      FaIcon: FontAwesomeIcon,
      FaLayers: FontAwesomeLayers,
      FaLayersText: FontAwesomeLayersText
    };

    this.plugins = [
      // Add Vue plugins:
      { plugin: router, config: {} },
      {
        plugin: createPinia(),
        config: {}
      },
      { plugin: ConfirmationService, config: {} },
      { plugin: ToastService, config: {} },
      ((): { plugin: Plugin; config: PrimeVueConfiguration } => ({
        plugin: PrimeVue,
        config: {
          theme: {
            preset: taiPreset,
            options: {
              // dark mode not currently implemented, but this is needed to override default dark theme being automatically applied by preset, which modifies styling undesirably.
              darkModeSelector: ".tai-theme-dark-mode"
            }
          },
          unstyled: true,
          pt: defaultTheme
        }
      }))(),
      { plugin: VueQueryPlugin, config: {} },
      { plugin: HighChartsPlugin, config: {} }
    ];

    this.directives = {
      "relative-time": relativeTimeDirective,
      "cell-formatter": cellFormatterDirective as Directive,
      "tai-tooltip": taiTooltip,
      autofocus: vAutofocus
    };
  }

  /**
   * Add vendor icons to core font awesome library
   */
  public injectFontAwesomeIcons() {
    library.add(...this.fontAwesomeIcons);
  }

  /**
   * Add all plugins to Vue App
   */
  public injectPlugins() {
    this.plugins.forEach(({ plugin, config }) => this.app.use(plugin, config));
  }

  /**
   * Add all vendor components to Vue App
   */
  public injectComponents() {
    for (const k in this.components) {
      this.app.component(k, this.components[k as keyof typeof this.components]);
    }
  }

  /**
   * Add all custom/vendor directives to Vue App
   */
  public injectDirectives() {
    for (const k in this.directives) {
      const directive = this.directives[k as keyof typeof this.directives];
      if (directive) {
        this.app.directive(k, directive);
      }
    }
  }

  /**
   * Configure Sentry for error tracking
   */
  public initializeSentry() {
    const tracesSampleRate = +import.meta.env.VITE_SENTRY_TRACE_SAMPLE_RATE;
    const replaysSessionSampleRate = +import.meta.env
      .VITE_SENTRY_REPLAY_SESSION_SAMPLE_RATE;
    const replaysOnErrorSampleRate = +import.meta.env
      .VITE_SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE;
    const release = import.meta.env.VITE_APP_BUILD;
    const environment = import.meta.env.VITE_ENVIRONMENT;

    console.info(`Initializing Sentry release: [${environment}:${release}]`);
    Sentry.init({
      app: this.app,
      dsn: import.meta.env.VITE_SENTRY_DSN,
      environment,
      release,
      integrations: [
        Sentry.browserTracingIntegration({ router }),
        Sentry.replayIntegration()
      ],
      // Tracing
      // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
      tracePropagationTargets: [
        import.meta.env.VITE_TAI_API_BASE_URL,
        "localhost"
      ],
      tracesSampleRate, //  Capture 100% of the transactions

      // Session Replay
      replaysSessionSampleRate, // You may want to change this to 100% while in development and then sample at a lower rate in production.
      replaysOnErrorSampleRate // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
    });
  }

  /**
   * Add all icon, component, and plugin dependencies to Vue app
   */
  public injectAppDependencies() {
    this.injectFontAwesomeIcons();
    this.injectPlugins();
    this.injectComponents();
    this.injectDirectives();
  }
}
